import { ThemeEvents } from "@theme/events";
import { formatMoney } from "@theme/money-formatting";

/**
 * PDP payment choice for deposit selling plans — see snippets/pdp-payment-plans.liquid
 * for why the theme draws it and how it shares the form with the STOQ app.
 *
 * In short: STOQ owns `selling_plan` and its line-item properties, and polls its own
 * (hidden) radios every 500 ms to decide which plan that is. So a choice made here is
 * handed to STOQ by checking ITS radio and firing `change` — the path its own listener
 * expects — and a choice STOQ makes (its default on load, its re-render on a variant
 * change) is read back from `stoq:preorder-payment-option:selected` and from the widget
 * it re-creates. Our own hidden input only speaks when STOQ is not running.
 */

const STOQ_RADIO =
  ".restock-rocket-payment-widget .restock-rocket-payment-input";
const STOQ_INPUT = 'input[name="selling_plan"][data-stoq-injected]';
const STOQ_SELECTED = "stoq:preorder-payment-option:selected";

class PaymentPlanPicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MutationObserver | undefined} */
  #formObserver;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener("change", this.#onChange, { signal });
    document.addEventListener(STOQ_SELECTED, this.#onStoqSelected, { signal });

    const section = this.closest(".shopify-section, dialog") ?? document;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });

    const form = this.form;
    if (form) {
      form.addEventListener(
        ThemeEvents.quantitySelectorUpdate,
        this.#renderAmounts,
        { signal },
      );
      form.addEventListener("change", this.#onFormChange, { signal });

      // STOQ swaps its hidden inputs (direct children of the form) and its widget (beside
      // the decoy, just above the form) on every variant or plan change.
      this.#formObserver = new MutationObserver(this.#onFormMutation);
      this.#formObserver.observe(form, { childList: true });
      const host = this.#host;
      if (host !== form) this.#formObserver.observe(host, { childList: true });
    }

    this.#refresh();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#formObserver?.disconnect();
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.closest("form");
  }

  /** @returns {HTMLInputElement[]} */
  get #radios() {
    return Array.from(this.querySelectorAll(".payment-plans__radio"));
  }

  /** @returns {HTMLInputElement | null} */
  get #input() {
    return this.querySelector("[data-payment-plan-input]");
  }

  /** The buy-buttons wrapper holding both the decoy anchor and the form. */
  get #host() {
    return this.closest("product-form-component") ?? this.form ?? this;
  }

  get #variantId() {
    return /** @type {HTMLInputElement | null} */ (
      this.form?.querySelector('input[name="id"]')
    )?.value;
  }

  #refresh() {
    this.#adoptStoqChoice();
    this.#renderAmounts();
    this.#reconcileInput();
  }

  /**
   * A shopper picked a plan here: mirror it into our input and hand it to STOQ.
   * @param {Event} event
   */
  #onChange = (event) => {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    if (!radio.matches?.(".payment-plans__radio") || !radio.checked) return;

    const input = this.#input;
    if (input) input.value = radio.value;

    const stoqRadio = this.#stoqRadio(radio.value);
    if (stoqRadio && !stoqRadio.checked) {
      stoqRadio.checked = true;
      stoqRadio.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.#reconcileInput();
  };

  /** @param {Event} event */
  #onFormChange = (event) => {
    if (/** @type {HTMLInputElement} */ (event.target).name === "quantity")
      this.#renderAmounts();
  };

  /** @param {CustomEvent} event */
  #onStoqSelected = (event) => {
    const planId = event.detail?.selectedSellingPlanId;
    if (planId) this.#select(String(planId));
  };

  /** @param {MutationRecord[]} records */
  #onFormMutation = (records) => {
    const widgetArrived = records.some((record) =>
      Array.from(record.addedNodes).some(
        (node) =>
          node instanceof Element &&
          node.classList.contains("restock-rocket-payment-widget"),
      ),
    );
    if (widgetArrived) this.#adoptStoqChoice();
    this.#reconcileInput();
  };

  /**
   * Same-product variant switches only morph the picker block, so this element has to
   * take the new variant's plans from the fetched section itself.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const { productId, html } = event.detail?.data ?? {};
    if (productId && String(productId) !== this.dataset.productId) return;

    const next = html?.querySelector?.("payment-plan-picker");
    const fieldset = this.querySelector("[data-payment-plans]");
    const nextFieldset = next?.querySelector("[data-payment-plans]");
    if (!next || !fieldset || !nextFieldset) {
      this.hidden = true;
      this.#reconcileInput();
      return;
    }

    fieldset.replaceWith(nextFieldset.cloneNode(true));
    this.hidden = next.hasAttribute("hidden");

    const input = this.#input;
    const nextInput = /** @type {HTMLInputElement | null} */ (
      next.querySelector("[data-payment-plan-input]")
    );
    if (input && nextInput) input.value = nextInput.value;

    this.#refresh();
  };

  /** @param {string} planId */
  #select(planId) {
    const radio = this.#radios.find((r) => r.value === planId);
    if (!radio) return;
    radio.checked = true;
    const input = this.#input;
    if (input) input.value = planId;
    this.#reconcileInput();
  }

  /** Line our radios up with whatever STOQ currently holds for this variant. */
  #adoptStoqChoice() {
    const config = window._RestockRocketConfig;
    const variantId = this.#variantId;
    const fromState = variantId
      ? config?.variantsSellingPlanId?.[variantId]
      : undefined;
    const fromWidget = /** @type {HTMLInputElement | null} */ (
      this.#scope().querySelector(`${STOQ_RADIO}:checked`)
    )?.value;
    const planId = fromState ?? fromWidget;
    if (planId) this.#select(String(planId));
  }

  /**
   * Our input carries the plan only while STOQ has not injected its own; two
   * `selling_plan` fields in one post would leave the cart guessing.
   */
  #reconcileInput() {
    const input = this.#input;
    if (!input) return;
    const stoqOwnsIt = !!this.form?.querySelector(STOQ_INPUT);
    input.disabled = this.hidden || stoqOwnsIt || !input.value;
  }

  /** @param {string} planId */
  #stoqRadio(planId) {
    return /** @type {HTMLInputElement | null} */ (
      this.#scope().querySelector(`${STOQ_RADIO}[value="${planId}"]`)
    );
  }

  /** STOQ's widget sits beside the decoy above our form; fall back to the page if it strayed. */
  #scope() {
    const host = this.#host;
    return host.querySelector(".restock-rocket-payment-widget")
      ? host
      : document;
  }

  #renderAmounts = () => {
    const { moneyFormat, currency } = this.dataset;
    if (!moneyFormat || !currency) return;

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.form?.querySelector('input[name="quantity"]')
    );
    const quantity = Math.max(
      1,
      parseInt(quantityInput?.value ?? "1", 10) || 1,
    );

    for (const radio of this.#radios) {
      const option = radio.closest(".payment-plans__option");
      if (!option) continue;

      const today = option.querySelector("[data-today-amount]");
      if (today)
        today.textContent = formatMoney(
          Number(radio.dataset.today) * quantity,
          moneyFormat,
          currency,
        );
    }
  };
}

if (!customElements.get("payment-plan-picker")) {
  customElements.define("payment-plan-picker", PaymentPlanPicker);
}
